import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, AlertTriangle, X } from "lucide-react";

/**
 * CA Access Consent Dialog
 *
 * Shown when:
 *  - A CA accepts a client's invitation (variant="ca-accept")
 *  - A client accepts a CA's invitation (variant="client-accept")
 *
 * Calls onConfirm(consent=true) only after the user clicks the green CTA.
 */
export default function CAConsentDialog({ open, onClose, onConfirm, variant = "ca-accept", inviterName = "" }) {
  if (!open) return null;

  const isClient = variant === "client-accept";
  const title = isClient
    ? "Grant data access to this CA / Professional?"
    : "Confirm access to client's data";
  const subtitle = isClient
    ? `${inviterName || "Your CA / Professional"} will be able to view your invoices and business data for filing, accounting, and management purposes.`
    : `By accepting, you'll be granted access to ${inviterName || "this client"}'s invoices and business data for filing, accounting, and management purposes.`;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
        data-testid="ca-consent-dialog"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl max-w-md w-full p-6 sm:p-7"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start gap-3 mb-5">
            <div className="w-11 h-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold text-slate-900 leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {title}
              </h2>
              <p className="text-sm text-slate-500 mt-1 leading-relaxed">{subtitle}</p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 flex items-start gap-2.5">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 leading-relaxed">
              By granting access, your invoices and related business data may be visible to this CA/professional for filing, accounting, and management purposes. <span className="font-semibold">You may revoke access anytime</span> from Settings &rarr; CA/Professional Management.
            </p>
          </div>

          <ul className="space-y-2 mb-6 text-xs text-slate-600">
            <li className="flex items-start gap-2"><span className="w-1 h-1 rounded-full bg-slate-400 mt-1.5" /> Access scope: invoices, customers, products, payments.</li>
            <li className="flex items-start gap-2"><span className="w-1 h-1 rounded-full bg-slate-400 mt-1.5" /> Every grant and revoke is recorded in the access log.</li>
            <li className="flex items-start gap-2"><span className="w-1 h-1 rounded-full bg-slate-400 mt-1.5" /> Either party can revoke at any time without notice.</li>
          </ul>

          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium"
              data-testid="consent-cancel-btn"
              data-test="ca-consent-cancel"
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(true)}
              className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
              data-testid="consent-confirm-btn"
              data-test="ca-consent-confirm"
            >
              I consent &mdash; Grant access
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
